import { TOOLBAR_OVERLAY_HEIGHT_PX, PANE_MARGIN_MAX_SHARE } from "../../constants";
import {
  POSITION_DIRECTION,
  POSITION_EDGE_GAP_PX,
  POSITION_LINE_GAP_PX,
  POSITION_PILL_LEFT_PX,
  POSITION_PIN_ARROW,
  POSITION_TONE,
} from "./positionConstants";

/**
 * Rows of the pane where position lines can stand. The top starts under
 * the React toolbar (it covers the top-left corner, where the pills are),
 * the bottom keeps the pill inside the pane.
 *
 * @param {{ height: number }} pane
 * @param {{ pillHeight: number }} sizes
 * @returns {{ top: number, bottom: number }} CSS px
 */
const lineBand = (pane, sizes) => {
  const halfPill = sizes.pillHeight / 2;
  const top = Math.min(TOOLBAR_OVERLAY_HEIGHT_PX, pane.height * PANE_MARGIN_MAX_SHARE) + halfPill;

  return { top, bottom: Math.max(top, pane.height - halfPill - POSITION_EDGE_GAP_PX) };
};

// A line outside the band is pinned to its edge (E11); `pin` tells where the real price is
const pinLine = (y, band) => {
  if (y < band.top) return { y: band.top, pin: POSITION_DIRECTION.UP };
  if (y > band.bottom) return { y: band.bottom, pin: POSITION_DIRECTION.DOWN };

  return { y, pin: null };
};

// Of the lines pinned to one edge only the one closest to the visible prices is drawn
const isCloserToView = (line, other) =>
  line.pin === POSITION_DIRECTION.UP ? line.realY > other.realY : line.realY < other.realY;

const keepClosestPinned = (lines) => {
  const closest = {};

  lines.forEach((line) => {
    if (line.pin && (!closest[line.pin] || isCloserToView(line, closest[line.pin]))) {
      closest[line.pin] = line;
    }
  });

  return lines.filter((line) => !line.pin || closest[line.pin] === line);
};

/**
 * Where the lines of the positions are in this frame. A line outside the
 * visible prices is pinned to the edge, and of the pinned lines at each
 * edge only the closest one is drawn (moon.com). The card of a position
 * stays on its entry line, pinned or not drawn, so it can always be closed.
 * Zones are shown only for lines that are not pinned. Each line starts a
 * small gap right of its own pill; a pinned line starts after its ▲ / ▼,
 * as far from it as the ▲ / ▼ is from the pill.
 *
 * @param {import("./positionsState").ChartPosition[]} positions
 * @param {object} params
 * @param {object} params.priceScale
 * @param {{ width: number, height: number }} params.pane
 * @param {object} params.sizes - from positionSizesFor
 * @param {(text: string, font: string) => number} params.measure
 * @param {string} params.pillFont
 * @param {boolean} params.withCards - false on phones, where positions have no cards
 * @returns {{
 *   lines: Array<{ tone: string, price: number, text: string, y: number, pin: string|null,
 *     pillWidth: number, startX: number }>,
 *   zones: Array<{ tone: string, y: number, direction: string }>,
 *   cards: Array<{ position: object, y: number }>,
 * }} y and x in CSS px of the pane
 */
export const resolvePositionScene = (positions, { priceScale, pane, sizes, measure, pillFont, withCards }) => {
  const band = lineBand(pane, sizes);
  const lines = [];
  const zones = [];
  const cards = [];

  const addLine = ({ tone, price, text }) => {
    const realY = priceScale.priceToY(price);
    const pillWidth = measure(text, pillFont) + 2 * sizes.pillPaddingX;
    const line = { tone, price, text, realY, pillWidth, ...pinLine(realY, band) };
    const pillRight = POSITION_PILL_LEFT_PX + pillWidth;
    const { gap, width: arrowWidth } = POSITION_PIN_ARROW;

    // Pinned: pill, gap, ▲ / ▼, the same gap, line
    line.startX = line.pin ? pillRight + gap + arrowWidth + gap : pillRight + POSITION_LINE_GAP_PX;
    lines.push(line);

    return line;
  };

  positions.forEach((position) => {
    const entryLine = addLine({
      tone: POSITION_TONE.ENTRY,
      price: position.entry.price,
      text: position.entry.label,
    });

    if (withCards) cards.push({ position, y: entryLine.y });
    position.levels.forEach(addLine);
    position.zones.forEach(({ tone, price, direction }) => {
      const y = priceScale.priceToY(price);

      if (y >= band.top && y <= band.bottom) zones.push({ tone, y, direction });
    });
  });

  return { lines: keepClosestPinned(lines), zones, cards };
};
